import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';

export default function RegisterSectionSignIn1() {
    const navigate = useNavigate();
    const recaptchaRef = useRef(null);
    
    // State management
    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        email: '',
        password: '',
        acceptTerms: false
    });
    const [recaptchaToken, setRecaptchaToken] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    // Google OAuth configuration
    const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || "30376606763-5v1mjl3g55sa29ph7ksipfp0477qi9ie.apps.googleusercontent.com";
    const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY || "";
    
    // Debug logging
    console.log('Current origin:', window.location.origin);
    console.log('Google Client ID:', GOOGLE_CLIENT_ID);

    // Initialize Google Identity Services
    useEffect(() => {
        const initializeGoogle = () => {
            if (window.google && window.google.accounts) {
                window.google.accounts.id.initialize({
                    client_id: GOOGLE_CLIENT_ID,
                    callback: handleGoogleCredentialResponse,
                    auto_select: false,
                    cancel_on_tap_outside: true,
                    use_fedcm_for_prompt: false
                });
                console.log('Google Identity Services initialized');
            } else {
                // Retry after a short delay if Google Identity Services isn't loaded yet
                setTimeout(initializeGoogle, 100);
            }
        };
        initializeGoogle();
    }, [GOOGLE_CLIENT_ID]);

    // Handle form input changes
    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    // Handle reCAPTCHA
    const handleRecaptchaChange = (token) => {
        setRecaptchaToken(token);
    };

    // Handle Google credential response
    const handleGoogleCredentialResponse = async (response) => {
        try {
            setLoading(true);
            setError('');
            
            // Send the credential to your backend
            const { buildUrl } = await import('../../services/api');
            const apiBase = buildUrl('');
            const result = await fetch(`${apiBase}/auth/google`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    credential: response.credential
                }),
            });

            const data = await result.json();

            if (result.ok) {
                // Store the JWT token
                localStorage.setItem('access_token', data.access_token);
                // Redirect to plan selection for new users
                navigate('/plan-selection');
            } else {
                setError(data.detail || 'Google sign-in failed. Please try again.');
            }
        } catch (error) {
            console.error('Google sign-in error:', error);
            setError('Google sign-in failed. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    // Store reCAPTCHA widget ID for reset functionality
    const [recaptchaWidgetId, setRecaptchaWidgetId] = useState(null);

    // Initialize reCAPTCHA Enterprise
    useEffect(() => {
        if (!RECAPTCHA_SITE_KEY) {
            console.error('REACT_APP_RECAPTCHA_SITE_KEY is empty or missing at build time');
            return;
        }
        // Global callback for reCAPTCHA
        window.onRecaptchaSuccess = (token) => {
            console.log('reCAPTCHA completed, token received');
            setRecaptchaToken(token);
        };

        const loadRecaptcha = () => {
            if (window.grecaptcha && window.grecaptcha.enterprise) {
                window.grecaptcha.enterprise.ready(() => {
                    const container = document.getElementById('recaptcha-container');
                    if (container && !container.hasChildNodes()) {
                        try {
                            console.log('Rendering reCAPTCHA Enterprise widget');
                            const widgetId = window.grecaptcha.enterprise.render('recaptcha-container', {
                                'sitekey': RECAPTCHA_SITE_KEY,
                                'callback': window.onRecaptchaSuccess,
                                'action': 'REGISTER' // Enterprise requires an action
                            });
                            setRecaptchaWidgetId(widgetId);
                            console.log('reCAPTCHA Enterprise widget rendered with ID:', widgetId);
                        } catch (error) {
                            console.error('reCAPTCHA Enterprise render error:', error);
                        }
                    }
                });
            } else if (window.grecaptcha && window.grecaptcha.ready) {
                // Fallback to regular reCAPTCHA if Enterprise is not available
                window.grecaptcha.ready(() => {
                    const container = document.getElementById('recaptcha-container');
                    if (container && !container.hasChildNodes()) {
                        try {
                            console.log('Rendering regular reCAPTCHA widget (fallback)');
                            const widgetId = window.grecaptcha.render('recaptcha-container', {
                                'sitekey': RECAPTCHA_SITE_KEY,
                                'callback': window.onRecaptchaSuccess
                            });
                            setRecaptchaWidgetId(widgetId);
                            console.log('Regular reCAPTCHA widget rendered with ID:', widgetId);
                        } catch (error) {
                            console.error('Regular reCAPTCHA render error:', error);
                        }
                    }
                });
            } else {
                // Retry after a short delay if reCAPTCHA isn't loaded yet
                console.log('reCAPTCHA not ready, retrying...');
                setTimeout(loadRecaptcha, 500);
            }
        };
        
        // Add a delay to ensure DOM is ready
        setTimeout(loadRecaptcha, 1000);

        // Cleanup
        return () => {
            if (window.onRecaptchaSuccess) {
                delete window.onRecaptchaSuccess;
            }
        };
    }, [RECAPTCHA_SITE_KEY]);

    // Regular email registration
    const handleEmailSignup = async (e) => {
        e.preventDefault();
        setError('');
        
        // Validation
        if (!formData.firstName || !formData.lastName || !formData.email || !formData.password) {
            setError('Please fill in all required fields.');
            return;
        }
        
        if (!formData.acceptTerms) {
            setError('Please accept the Terms and Conditions.');
            return;
        }
        
        if (!recaptchaToken) {
            setError('Please complete the reCAPTCHA verification.');
            return;
        }

        setLoading(true);
        
        try {
            const { buildUrl } = await import('../../services/api');
            const apiBase = buildUrl('');
            
            const requestData = {
                email: formData.email,
                password: formData.password,
                full_name: `${formData.firstName} ${formData.lastName}`,
                role: 'user',
                recaptcha_response: recaptchaToken
            };
            
            console.log('Sending registration request to:', `${apiBase}/register`);
            console.log('Request data:', requestData);
            
            const response = await fetch(`${apiBase}/register`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(requestData),
            });

            console.log('Registration response status:', response.status);
            console.log('Registration response headers:', response.headers);
            
            const data = await response.json();
            console.log('Registration response data:', data);

            if (response.ok) {
                // Registration successful, store token and redirect to plan selection
                if (data.access_token) {
                    localStorage.setItem('access_token', data.access_token);
                }
                navigate('/plan-selection');
            } else {
                setError(data.detail || 'Registration failed. Please try again.');
                // Reset reCAPTCHA
                if (recaptchaWidgetId !== null) {
                    if (window.grecaptcha && window.grecaptcha.enterprise) {
                        window.grecaptcha.enterprise.reset(recaptchaWidgetId);
                    } else if (window.grecaptcha) {
                        window.grecaptcha.reset(recaptchaWidgetId);
                    }
                }
                setRecaptchaToken('');
            }
        } catch (error) {
            console.error('Registration error:', error);
            setError('Network error. Please try again.');
            // Reset reCAPTCHA
            if (recaptchaWidgetId !== null) {
                if (window.grecaptcha && window.grecaptcha.enterprise) {
                    window.grecaptcha.enterprise.reset(recaptchaWidgetId);
                } else if (window.grecaptcha) {
                    window.grecaptcha.reset(recaptchaWidgetId);
                }
            }
            setRecaptchaToken('');
        } finally {
            setLoading(false);
        }
    };

    // Google Sign-In with account chooser
    const handleGoogleSignIn = () => {
        if (!window.google || !window.google.accounts) {
            setError('Google Sign-In is not ready. Please try again.');
            return;
        }
        
        try {
            console.log('Attempting Google Sign-In with account chooser...');
            setError(''); // Clear any previous errors
            
            // Use the OAuth popup flow for account selection
            window.google.accounts.oauth2.initTokenClient({
                client_id: GOOGLE_CLIENT_ID,
                scope: 'openid email profile',
                callback: async (response) => {
                    if (response.access_token) {
                        try {
                            // Get user info from Google
                            const userInfoResponse = await fetch(`https://www.googleapis.com/oauth2/v2/userinfo?access_token=${response.access_token}`);
                            const userInfo = await userInfoResponse.json();
                            
                            // Create a credential-like object for our backend
                            const credential = {
                                email: userInfo.email,
                                name: userInfo.name,
                                google_id: userInfo.id,
                                picture: userInfo.picture
                            };
                            
                            // Call our backend
                            const { buildUrl } = await import('../../services/api');
                            const apiBase = buildUrl('');
                            const result = await fetch(`${apiBase}/auth/google-oauth`, {
                                method: 'POST',
                                headers: {
                                    'Content-Type': 'application/json',
                                },
                                body: JSON.stringify(credential),
                            });

                            const data = await result.json();

                            if (result.ok) {
                                // Store the JWT token
                                localStorage.setItem('access_token', data.access_token);
                                
                                // Check user's subscription status to determine where to redirect
                                try {
                                    const subResponse = await fetch(`${apiBase}/subscription`, {
                                        headers: {
                                            'Authorization': `Bearer ${data.access_token}`
                                        }
                                    });
                                    
                                    if (subResponse.ok) {
                                        const subData = await subResponse.json();
                                        console.log('🔍 Google OAuth - Subscription check:', subData);
                                        
                                        // Check if user has an active subscription
                                        const hasActivePlan = subData.subscription_status === 'active' && 
                                                             (subData.plan_type === 'individual' || subData.plan_type === 'family');
                                        
                                        if (hasActivePlan) {
                                            console.log('✅ Existing user with active subscription - redirecting to dashboard');
                                            navigate('/dashboard');
                                        } else {
                                            console.log('👤 New user or inactive subscription - redirecting to plan selection');
                                            navigate('/plan-selection');
                                        }
                                    } else {
                                        // If subscription check fails, default to plan selection
                                        console.log('⚠️ Subscription check failed - redirecting to plan selection');
                                        navigate('/plan-selection');
                                    }
                                } catch (error) {
                                    console.error('Subscription check error:', error);
                                    // Default to plan selection if subscription check fails
                                    navigate('/plan-selection');
                                }
                            } else {
                                setError(data.detail || 'Google sign-in failed. Please try again.');
                            }
                        } catch (error) {
                            console.error('Google OAuth error:', error);
                            setError('Google sign-in failed. Please try again.');
                        }
                    }
                }
            }).requestAccessToken();
            
        } catch (error) {
            console.error('Google Sign-In initialization error:', error);
            setError('Google Sign-In failed to initialize. Please check the console for details.');
        }
    };

    return (
        <React.Fragment>
            <>
                <section className="relative py-20 min-h-screen">
  <img className="hidden lg:block absolute top-0 left-0 mt-16 z-10" src="zeus-assets/icons/dots/blue-dot-left-bars.svg" alt />
  <div className="absolute top-0 left-0 bottom-0 w-full bg-gray-50" />
  <div className="relative container px-4 mx-auto">
    <div className="flex flex-col items-center justify-center">
      <div className="w-full max-w-lg px-4 mb-12 text-center">
        <h2 className="mb-10 text-4xl font-semibold font-heading">Sign up for Ordio!</h2>
        <p className="text-xl text-gray-500">Join our community and enjoy intelligent sorting and AI powered organization.</p>
      </div>
      <div className="w-full max-w-md px-4">
        <div className="p-6 lg:px-10 lg:py-12 bg-white text-center border rounded-xl">
          <form onSubmit={handleEmailSignup}>
            <span className="inline-block mb-4 text-xs text-blue-400 font-semibold">Sign Up</span>
            <h3 className="mb-12 text-3xl font-semibold font-heading">Join our community</h3>
            
            {error && (
              <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
                {error}
              </div>
            )}
            <div className="relative flex flex-wrap mb-6">
              <input 
                className="relative mb-2 md:mb-0 w-full py-4 pl-4 text-sm border rounded" 
                type="text" 
                name="firstName"
                value={formData.firstName}
                onChange={handleInputChange}
                placeholder="John" 
                required
              />
              <span className="absolute top-0 left-0 ml-4 -mt-2 px-1 inline-block bg-white text-gray-500 text-xs">First Name</span>
            </div>
            <div className="relative flex flex-wrap mb-6">
              <input 
                className="relative mb-2 md:mb-0 w-full py-4 pl-4 text-sm border rounded" 
                type="text" 
                name="lastName"
                value={formData.lastName}
                onChange={handleInputChange}
                placeholder="Doe" 
                required
              />
              <span className="absolute top-0 left-0 ml-4 -mt-2 px-1 inline-block bg-white text-gray-500 text-xs">Last Name</span>
            </div>
            <div className="relative flex flex-wrap mb-6">
              <input 
                className="relative mb-2 md:mb-0 w-full py-4 pl-4 text-sm border rounded" 
                type="email" 
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                placeholder="e.g hello@shuffle.dev" 
                required
              />
              <span className="absolute top-0 left-0 ml-4 -mt-2 px-1 inline-block bg-white text-gray-500 text-xs">Your email address</span>
            </div>
            <div className="relative flex flex-wrap mb-6">
              <input 
                className="relative mb-2 md:mb-0 w-full py-4 pl-4 text-sm border rounded" 
                type="password" 
                name="password"
                value={formData.password}
                onChange={handleInputChange}
                placeholder="******" 
                required
                minLength="6"
              />
              <span className="absolute top-0 left-0 ml-4 -mt-2 px-1 inline-block bg-white text-gray-500 text-xs">Password</span>
            </div>
            <div className="mb-6">
              <div className="flex items-start text-left">
                <input 
                  type="checkbox" 
                  id="terms" 
                  name="acceptTerms"
                  checked={formData.acceptTerms}
                  onChange={handleInputChange}
                  className="mt-1 mr-3 h-4 w-4 text-red-400 border-gray-300 rounded focus:ring-red-400" 
                  required
                />
                <label htmlFor="terms" className="text-sm text-gray-500">
                  I agree to the <a href="/terms-and-conditions" target="_blank" rel="noopener noreferrer" className="text-red-400 hover:underline">Terms and Conditions</a>
                </label>
              </div>
            </div>
            <div className="mb-6">
              <div className="flex justify-center">
                <div 
                  id="recaptcha-container"
                  className="g-recaptcha" 
                  data-sitekey={RECAPTCHA_SITE_KEY}
                  data-callback="onRecaptchaSuccess"
                ></div>
              </div>
            </div>
            <button 
              type="submit" 
              disabled={loading}
              className={`w-full inline-block py-4 mb-4 text-sm text-white font-medium leading-normal rounded transition duration-200 text-center ${
                loading 
                  ? 'bg-gray-400 cursor-not-allowed' 
                  : 'bg-red-400 hover:bg-red-300'
              }`}
            >
              {loading ? 'Creating Account...' : 'Sign up with email'}
            </button>
            <div 
              id="google-signin-button"
              onClick={handleGoogleSignIn}
              className={`w-full inline-flex items-center justify-center py-4 mb-4 text-sm font-medium leading-normal border rounded transition duration-200 cursor-pointer ${
                loading 
                  ? 'text-gray-400 bg-gray-100 border-gray-200 cursor-not-allowed' 
                  : 'text-gray-700 bg-white hover:bg-gray-50 border-gray-300'
              }`}
            >
              <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              {loading ? 'Signing in...' : 'Sign up with Google'}
            </div>
            <div className="text-center">
              <span className="block text-sm text-gray-500">Already have an account? <Link to="/login" className="text-red-400 hover:underline">Sign in</Link></span>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
</section>


            </>
        </React.Fragment>
    );
}

